// "How can Klarity help your practice?" 选项定义
export enum EHelpType {
    CREDENTIALING = 'credentialing',
    INSURANCE_BILLING = 'insurance-billing',
    PATIENT_GROWTH = 'patient-growth',
    EHR = 'ehr',
}

// 路由优先级排序：Credentialing > Insurance billing > Patient growth > EHR
export const INTENT_ORDER: EHelpType[] = [
    EHelpType.CREDENTIALING,
    EHelpType.INSURANCE_BILLING,
    EHelpType.PATIENT_GROWTH,
    EHelpType.EHR,
];

export type THelpTypeOption = {
    id: EHelpType;
    title: string;
    description: string;
    badge?: {
        text: string;
        type: 'free' | 'popular';
    };
};

export const HELP_TYPE_OPTIONS: THelpTypeOption[] = [
    {
        id: EHelpType.CREDENTIALING,
        title: 'Credentialing',
        description:
            'Get credentialed with major insurance payers while keeping ownership of your payer contracts. Start with a small, refundable fee per panel.',
        badge: { text: 'Most popular', type: 'popular' },
    },
    {
        id: EHelpType.INSURANCE_BILLING,
        title: 'Insurance billing',
        description:
            'We file, track and follow up on your claims for 3.9% of reimbursement. That is about half the industry average.',
    },
    {
        id: EHelpType.PATIENT_GROWTH,
        title: 'Patient growth',
        description:
            'Get found and booked on the Klarity Health marketplace. You only pay when a patient books.',
    },
    {
        id: EHelpType.EHR,
        title: 'EHR',
        description:
            'Run your whole practice in one place: scheduling, charting, e-prescribing and telehealth.',
        badge: { text: 'Free', type: 'free' },
    },
];

// "What you're getting" 面板内容
export type TIntentCartItem = {
    name: string;
    desc: string;
    fit: string[];
};

export const INTENT_CART: Record<string, TIntentCartItem> = {
    [EHelpType.CREDENTIALING]: {
        name: 'Credentialing',
        desc: 'Expand your insurance coverage without managing the credentialing process on your own. We help manage your applications with major insurance payers from start to finish, while your payer contracts stay with your practice. Credentialing starts with a small, refundable fee per panel, and our cash back program can make credentialing with your first three panels essentially free.',
        fit: [
            'Providers not yet on insurance panels or looking to add new payers.',
            'Providers who want to own their payer contracts and build their insurance relationships directly.',
            'Practices looking for more independence from platforms like Headway, Grow Therapy, or Alma.',
            'Solo providers going out on their own from a group practice.',
        ],
    },
    [EHelpType.INSURANCE_BILLING]: {
        name: 'Insurance billing',
        desc: 'We file, track and follow up on every claim for 3.9% of reimbursement. We only get paid when you get paid.',
        fit: [
            'Already-credentialed providers tired of chasing claims and denials.',
            'Providers who are frustrated by their billing agents.',
            'Solo and small practices with no biller on staff.',
        ],
    },
    [EHelpType.PATIENT_GROWTH]: {
        name: 'Patient growth',
        desc: 'Customize the services you offer and get connected with new patients seeking the care you already provide. Patients can find your profile and book directly through the Klarity Health Marketplace. You only pay when a new patient books.',
        fit: [
            'New or growing practices with open slots to fill.',
            "Providers who want new patients but don't want to pay Google / Meta for impressions only.",
            'Providers who see slow patient referral growth from Headway / Grow Therapy / Psychology Today / Zocdoc.',
            'Anyone expanding into a new state or a new service line.',
        ],
    },
    [EHelpType.EHR]: {
        name: 'EHR',
        desc: 'A free, all-in-one Klarity EHR to run your whole practice. Scheduling, documentation, e-prescribing, telehealth, and patient messaging.',
        fit: [
            'Providers starting out without an EHR, or paying too much for one.',
            'Practices juggling separate tools for notes, scheduling and telehealth.',
            'Practices who want their patients to have a great experience scheduling and messaging with them.',
        ],
    },
};

// 解析逗号分隔的 helpType 字符串为数组（兼容老数据）
export const parseHelpTypes = (helpType?: string): string[] => {
    if (!helpType) return [];
    // 老数据兼容
    if (helpType === 'credentialing-billing') return [EHelpType.CREDENTIALING];
    if (helpType === 'all') return [EHelpType.EHR];
    return helpType.split(',').map((s) => s.trim()).filter(Boolean);
};

// 检查 helpType 是否包含指定 intent
export const hasIntent = (helpType: string | undefined, intent: EHelpType): boolean => {
    if (!helpType) return false;
    return parseHelpTypes(helpType).includes(intent);
};

// 是否选择了 credentialing 路径（兼容老数据：credentialing-billing 视为 credentialing，all 视为 ehr）
export const isCredentialingHelpType = (helpType?: string): boolean => {
    if (!helpType) return false;
    if (helpType === 'credentialing-billing') return true;
    return hasIntent(helpType, EHelpType.CREDENTIALING);
};
